// ============================================================
// Fichiers de récompenses de PremiumMod : la BOX à clés et la LOOTBOX sans clé
// ============================================================
// Les deux partagent la même entrée de récompense ({Weight, RarityId, Item | Money | Experience,
// IsBlock, IsTool}) et ne diffèrent que par la clé du tableau, les clés annexes et le dossier où le
// mod les lit. Le serveur applique la même liste blanche (ContainersService.LOOT_FORMATS) : la page
// ne peut écrire que ces clés-là.
// ============================================================

export type LootKind = 'box' | 'lootbox';

export interface LootFormat {
  kind: LootKind;
  /** Clé du tableau des entrées à la racine du fichier. */
  arrayKey: 'Loots' | 'Rewards';
  /** Clés racine gérées par la page (Id, le tableau, les annexes), dans l'ordre d'écriture. */
  keys: string[];
  /** Dossier où le mod lit ces fichiers, pour l'aide et la recherche manuelle. */
  folder: string;
  /** Libellés : nom au singulier, au pluriel, et article (« une box », « une lootbox »). */
  noun: string;
  nounPlural: string;
  idPlaceholder: string;
  /** Libellé de chaque clé racine, pour dire sur quoi un container diffère. */
  labels: Record<string, string>;
}

export const LOOT_FORMATS: Record<LootKind, LootFormat> = {
  box: {
    kind: 'box',
    arrayKey: 'Loots',
    keys: ['Id', 'Loots'],
    folder: 'data/mods/PremiumMod/LootBox',
    noun: 'box',
    nounPlural: 'box',
    idPlaceholder: 'paladium:amethyst_lootbox',
    labels: { Id: 'Id', Loots: 'entrées de butin' },
  },
  lootbox: {
    kind: 'lootbox',
    arrayKey: 'Rewards',
    keys: ['Id', 'Rewards', 'Count'],
    folder: 'data/mods/PremiumMod/LootBoxes',
    noun: 'lootbox',
    nounPlural: 'lootbox',
    idPlaceholder: 'ngpremium:lootbox_classique',
    labels: { Id: 'Id', Rewards: 'récompenses', Count: 'nombre de tirages' },
  },
};

/** Lootbox attendues sur NationsGlory (créées vides par une release de NationsGloryBE-World-Container). */
export const NG_EXPECTED_LOOTBOXES = ['ngpremium:lootbox_classique', 'ngpremium:lootbox_nuke', 'ngpremium:lootbox_legendaire', 'ngpremium:lootbox_ultimate', 'ngpremium:lootbox_megalodon'];

export const RARITIES = ['common', 'rare', 'epic', 'legendary'];

/** Animation d'ouverture d'une lootbox, choisie par la rareté tirée. */
export const LOOTBOX_ANIMATION: Record<string, number> = { common: 1, rare: 2, epic: 3, legendary: 4 };

/** Garde-fou du serveur sur Count (ContainersService.LOOT_MAX_COUNT). */
export const MAX_COUNT = 100;

/** Enchantement au format canonique du mod : identifiant du registre et niveau. */
export interface Enchant { Identifier: string; Level: number }

export interface LootItemDef {
  Identifier?: string;
  DisplayName?: string;
  Amount?: number;
  Damage?: number;
  Enchants?: Enchant[];
  Lore?: string[];
  [k: string]: unknown;
}

export interface LootEntry {
  Weight?: number;
  RarityId?: string;
  RarityIndex?: number;
  Item?: LootItemDef;
  Money?: number;
  Experience?: number;
  IsTool?: boolean;
  IsBlock?: boolean;
  [k: string]: unknown;
}

/** Contenu géré d'un fichier : Id, le tableau (sous sa clé) et les annexes. */
export interface LootFile {
  Id?: string;
  Count?: number;
  [k: string]: unknown;
}

export type RewardKind = 'item' | 'money' | 'xp';

export function kindOf(e: LootEntry): RewardKind {
  if (typeof e.Money === 'number') return 'money';
  if (typeof e.Experience === 'number') return 'xp';
  return 'item';
}

/** Id non vide d'un fichier, tel que le serveur le compare (texte). */
export function idOf(file: LootFile | null | undefined): string | undefined {
  const id = file?.Id;
  return id != null && String(id).trim() ? String(id) : undefined;
}

export function entriesOf(file: LootFile | null | undefined, format: LootFormat): LootEntry[] {
  const list = file?.[format.arrayKey];
  return Array.isArray(list) ? (list as LootEntry[]) : [];
}

/** Enchantements Bedrock proposés à la saisie (saisie libre toujours possible). */
export const ENCHANT_IDS = [
  'minecraft:protection', 'minecraft:fire_protection', 'minecraft:feather_falling', 'minecraft:blast_protection',
  'minecraft:projectile_protection', 'minecraft:thorns', 'minecraft:respiration', 'minecraft:depth_strider',
  'minecraft:aqua_affinity', 'minecraft:sharpness', 'minecraft:smite', 'minecraft:bane_of_arthropods',
  'minecraft:knockback', 'minecraft:fire_aspect', 'minecraft:looting', 'minecraft:efficiency',
  'minecraft:silk_touch', 'minecraft:unbreaking', 'minecraft:fortune', 'minecraft:power', 'minecraft:punch',
  'minecraft:flame', 'minecraft:infinity', 'minecraft:luck_of_the_sea', 'minecraft:lure', 'minecraft:frost_walker',
  'minecraft:mending', 'minecraft:binding', 'minecraft:vanishing', 'minecraft:impaling', 'minecraft:riptide',
  'minecraft:loyalty', 'minecraft:channeling', 'minecraft:multishot', 'minecraft:piercing', 'minecraft:quick_charge',
  'minecraft:soul_speed', 'minecraft:swift_sneak',
];

/**
 * Noms de l'ancien format Enchant [{id, power}] (noms Java, dont ceux de la 1.6) vers l'identifiant
 * Bedrock attendu par Enchants. Un nom inconnu sans espace de noms reçoit « minecraft: ».
 */
const LEGACY_ENCHANT_IDS: Record<string, string> = {
  durability: 'unbreaking',
  'protect.all': 'protection',
  'protect.fire': 'fire_protection',
  'protect.fall': 'feather_falling',
  'protect.explosion': 'blast_protection',
  'protect.projectile': 'projectile_protection',
  oxygen: 'respiration',
  waterworker: 'aqua_affinity',
  'damage.all': 'sharpness',
  'damage.undead': 'smite',
  'damage.arthropods': 'bane_of_arthropods',
  fire: 'fire_aspect',
  lootbonus: 'looting',
  digging: 'efficiency',
  untouching: 'silk_touch',
  lootbonusdigger: 'fortune',
  arrowdamage: 'power',
  arrowknockback: 'punch',
  arrowfire: 'flame',
  arrowinfinite: 'infinity',
};

export function canonicalEnchantId(raw: unknown): string {
  const id = String(raw ?? '').trim();
  if (!id || id.includes(':')) return id;
  return `minecraft:${LEGACY_ENCHANT_IDS[id.toLowerCase()] ?? id}`;
}

/**
 * Entrée relue dans le format canonique : un ancien Enchant [{id, power}] devient Enchants
 * [{Identifier, Level}] (fusionné avec un Enchants déjà présent). Le reste de l'entrée, clés
 * inconnues comprises, est conservé tel quel. `converted` dit si l'ancien format était là.
 */
export function normalizeEntry(e: LootEntry): { entry: LootEntry; converted: boolean } {
  const item = e.Item;
  if (!item || !Array.isArray(item.Enchant)) return { entry: e, converted: false };
  const current = Array.isArray(item.Enchants) ? item.Enchants : [];
  const legacy = (item.Enchant as Array<Record<string, unknown>>).map((x) => ({
    Identifier: canonicalEnchantId(x?.id ?? x?.Identifier),
    Level: Number(x?.power ?? x?.Level) || 1,
  }));
  const merged = [...current];
  for (const en of legacy) {
    if (en.Identifier && !merged.some((m) => m.Identifier === en.Identifier)) merged.push(en);
  }
  const next: LootItemDef = { ...item };
  delete next.Enchant;
  if (merged.length > 0) next.Enchants = merged; else delete next.Enchants;
  return { entry: { ...e, Item: next }, converted: true };
}

/** Identifiant proposé pour la clé d'une box : paladium:amethyst_lootbox → paladium:amethyst_key_lootbox. */
export function suggestKeyId(boxId: string): string {
  const [ns, name] = boxId.includes(':') ? boxId.split(':', 2) : ['paladium', boxId];
  const base = (name ?? '').replace(/[^a-zA-Z0-9_]/g, '_');
  const key = /_lootbox$/.test(base) ? base.replace(/_lootbox$/, '_key_lootbox') : `${base}_key`;
  return `${ns.replace(/[^a-zA-Z0-9_]/g, '_')}:${key}`;
}
